import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useShopHeaderImage from '@hooks/images/useShopHeaderImage';
import { isClosed, willOpenLater, getShopFullAddress } from '@utils/shop';
import { ORIGIN_POSITION } from '@shopDetails/tracking/actions/trackShopInfoClicked';
import PickupToggle from '@shopDetails/components/Pickup/PickupToggle';
import ShopInformation from '../../ShopInformation';
// Old styles
import {
  Closed,
  ContainerRelative,
  HeaderContainer,
  OpenIn,
  SubHeaderContainer,
  BackgroundImage,
} from '../../MainHeaderRestaurant/Mobile/components';
// New styles
import {
  Container,
  Title,
  Wrapper,
  LinkShopInfo,
  MenuSections,
  ArrowRight,
  MenuSectionContainer,
} from './components';
import MobileHeaderShimmer from './components/Shimmers/MobileHeaderShimmer';

const HEIGHT_BACKGROUND = 48;

function MainHeaderRestaurantPickup({
  shop,
  reviewsRoute,
  shopInfoRoute,
  show,
  subHeader,
  children,
  onFocusSearch,
  onSelectTab,
  onClickMenu,
  trackShopInfoClicked,
  discountsTotalAmount,
  plusSettings,
  showShimmer,
  isPickupFlagEnabled,
  isLogisticsDelivery,
  weeklyDiscount,
  pickupDiscount,
}) {
  const backgroundImage = useShopHeaderImage(shop);
  const isClosedShop = useMemo(() => isClosed(shop), [shop]);
  const willOpenLaterShop = useMemo(() => willOpenLater(shop), [shop]);
  const currentAddress = useMemo(() => getShopFullAddress(shop), [shop]);

  const trackShopInfoClick = useCallback(
    () => trackShopInfoClicked({ origin: ORIGIN_POSITION.ADDRESS }),
    [trackShopInfoClicked],
  );

  return (
    <Container show={show}>
      {showShimmer ? (
        <MobileHeaderShimmer />
      ) : (
        <>
          <BackgroundImage
            src={backgroundImage}
            isClosed={isClosedShop}
            height={`${HEIGHT_BACKGROUND}px`}
            withBlur
          />
          <ContainerRelative>
            <HeaderContainer>
              {isClosedShop && <Closed />}
              {willOpenLaterShop && <OpenIn nextHour={shop.nextHour} />}
              <Wrapper>
                {Boolean(shop?.name) && <Title>{shop.name}</Title>}
                {currentAddress && (
                  <LinkShopInfo onClick={trackShopInfoClick} to={shopInfoRoute}>
                    {currentAddress}
                    <ArrowRight size="xsmall" />
                  </LinkShopInfo>
                )}
                <ShopInformation
                  shop={shop}
                  reviewsRoute={reviewsRoute}
                  discountsTotalAmount={discountsTotalAmount}
                  plusSettings={plusSettings}
                  isPickupFlagEnabled={isPickupFlagEnabled}
                  isLogisticsDelivery={isLogisticsDelivery}
                  weeklyDiscount={weeklyDiscount}
                  pickupDiscount={pickupDiscount}
                />
              </Wrapper>
              {!isClosedShop && (
                <PickupToggle
                  deliveryType={shop?.deliveryType}
                  plusSettings={plusSettings}
                />
              )}
            </HeaderContainer>
            {children}
          </ContainerRelative>
        </>
      )}
      <MenuSectionContainer>
        <MenuSections
          onFocusSearch={onFocusSearch}
          onSelectTab={onSelectTab}
          onClickMenu={onClickMenu}
        />
      </MenuSectionContainer>
      {subHeader && <SubHeaderContainer>{subHeader}</SubHeaderContainer>}
    </Container>
  );
}

MainHeaderRestaurantPickup.propTypes = {
  show: PropTypes.bool,
  shop: PropTypes.object.isRequired,
  reviewsRoute: PropTypes.string.isRequired,
  shopInfoRoute: PropTypes.string.isRequired,
  subHeader: PropTypes.node,
  children: PropTypes.node,
  onFocusSearch: PropTypes.func.isRequired,
  onSelectTab: PropTypes.func.isRequired,
  onClickMenu: PropTypes.func,
  trackShopInfoClicked: PropTypes.func.isRequired,
  discountsTotalAmount: PropTypes.number,
  plusSettings: PropTypes.object,
  showShimmer: PropTypes.bool,
  isPickupFlagEnabled: PropTypes.bool,
  isLogisticsDelivery: PropTypes.bool,
  weeklyDiscount: PropTypes.object,
  pickupDiscount: PropTypes.object,
};

MainHeaderRestaurantPickup.defaultProps = {
  show: true,
  showShimmer: false,
};

export default MainHeaderRestaurantPickup;
